import { Component } from '@theme/component';
import { OverflowList } from '@theme/critical';
import { SlideshowSelectEvent, ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
      customElements.whenDefined('slideshow-component').then(() => {
        this.#getNextColorSlide()?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
        this.#setupMobileColorCycle();
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#destroyColorCycle();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    // this.#updateNonVariantImage()
    this.#previousSlideIndex = null;

    // Ensure our selected swatch remains checked after morphing
    const selectedId = this.variantPicker?.dataset.currentSelectedOptionId;
    if (selectedId) {
      try {
        this.variantPicker?.updateSelectedOption(selectedId);
      } catch (e) {
        // ignore if option is not present
      }
    }

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;
    console.log('Selected Image ID:', selectedImageId);

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      const targetSlide = slides.find((s) => s.getAttribute('slide-id') === selectedImageId);
      const targetImg = targetSlide?.querySelector('img');

      const finalize = () => {
        // Keep generic slides visible so arrows remain functional on cards
        for (const slide of slides) {
          if (slide.getAttribute('slide-id') !== selectedImageId) {
            slide.hidden = true;
            slide.setAttribute('aria-hidden', 'true');
          } else {
            slide.hidden = false;
            console.log('Revealing slide:', slide);
            // set aria-hidden to false for accessibility
            slide.setAttribute('aria-hidden', 'false');
          }
        }
        // Focus the newly selected variant image without collapsing the gallery
        slideshow.select({ id: selectedImageId }, undefined, { animate: false });

        requestAnimationFrame(() => {
          for (const slide of slides) {
            if (!slide.hasAttribute('variant-image')) {
              slide.hidden = false;
              slide.setAttribute('aria-hidden', 'false');
            }
          }
        });

        // Fix: ensure all media containers become visible after variant switch animation
        setTimeout(function () {
          slideshow.querySelectorAll('.product-media-container').forEach(function (e) {
            if (e.hasAttribute('hidden')) {
              e.removeAttribute('hidden');
            }
          });
        }, 800);
      };

      // Ensure the selected image is loaded before we hide others to prevent a white flash
      if (targetImg instanceof HTMLImageElement) {
        // Remove lazy loading and prioritize the fetch
        if (targetImg.getAttribute('loading') === 'lazy') targetImg.removeAttribute('loading');
        targetImg.setAttribute('fetchpriority', 'high');

        if (!targetImg.complete) {
          // Reveal target slide immediately but keep the current slide visible until the image is ready
          if (targetSlide) {
            targetSlide.hidden = false;
          }
          targetImg.addEventListener('load', finalize, { once: true });
          return;
        }
      }

      finalize(); 
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    console.log('Previewing variant with ID:', id);
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /** The card currently cycling colorways — only one animates at a time. */
  /** @type {ProductCard | null} */
  static #activeCycleCard = null;

  /** @type {{ overlay: HTMLElement, observer: IntersectionObserver, stop: () => void, deactivate: () => void, removeListeners: () => void } | null} */
  #colorCycle = null;

  /**
   * On touch devices there is no hover, so crossfade the card image through the design's
   * other colorways — but only after the customer "highlights" this card with a light tap
   * (the tap that shows hover styles without navigating). One card cycles at a time.
   * Active only when the "next color on hover" mode is enabled. Respects prefers-reduced-motion.
   */
  #setupMobileColorCycle() {
    if (this.#colorCycle) return;
    if (!window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { cardGallery, slideshow } = this.refs;
    if (cardGallery?.dataset.hoverImageStyle !== 'next_color') return;

    const sources = this.#getColorSlides()
      .map((slide) => ({ slide, img: slide.querySelector('img') }))
      .filter(({ img }) => img?.getAttribute('src'))
      .map(({ slide, img }) => ({
        id: slide.getAttribute('slide-id'),
        src: img.getAttribute('src'),
        srcset: img.getAttribute('srcset'),
        sizes: img.getAttribute('sizes'),
      }));

    // Cycle to the merchant's preferred (most contrasting) colorway first when one is set
    const preferredSwapId = cardGallery.dataset.preferredSwapId;
    if (preferredSwapId) {
      const preferredIndex = sources.findIndex((source) => source.id === preferredSwapId);
      if (preferredIndex > 1) {
        const [preferred] = sources.splice(preferredIndex, 1);
        sources.splice(1, 0, preferred);
      }
    }

    if (sources.length < 2 || !slideshow) return;

    const overlay = document.createElement('div');
    overlay.className = 'color-cycle-overlay';
    const imgs = [document.createElement('img'), document.createElement('img')];
    for (const img of imgs) {
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      overlay.append(img);
    }
    slideshow.append(overlay);

    const HOLD = 1800;
    const FIRST_HOLD = 0;
    const FADE = 700;

    let index = 0;
    let active = 0;
    let running = false;
    let firstRun = true;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;

    const step = () => {
      if (!running) return;
      // Start loading the next colorway right away so the swap isn't delayed by the network
      const nextIndex = (index + 1) % sources.length;
      const source = sources[nextIndex];
      const back = imgs[1 - active];
      if (source.srcset) back.srcset = source.srcset;
      if (source.sizes) back.sizes = source.sizes;
      back.src = source.src ?? '';
      const ready = back.decode().catch(() => {});

      timer = setTimeout(
        async () => {
          await ready;
          if (!running) return;
          firstRun = false;
          back.style.transition = 'none';
          back.style.opacity = '0';
          back.style.zIndex = '2';
          imgs[active].style.zIndex = '1';
          requestAnimationFrame(() => {
            back.style.transition = '';
            back.style.opacity = '1';
            timer = setTimeout(() => {
              const front = imgs[active];
              front.style.transition = 'none';
              front.style.opacity = '0';
              requestAnimationFrame(() => {
                front.style.transition = '';
              });
              active = 1 - active;
              index = nextIndex;
              step();
            }, FADE + 100);
          });
        },
        firstRun ? FIRST_HOLD : HOLD
      );
    };

    const stop = () => {
      running = false;
      clearTimeout(timer);
    };

    // Settle back to the product's real colorway and stop animating
    const deactivate = () => {
      stop();
      index = 0;
      active = 0;
      firstRun = true; // respond quickly to the next tap
      for (const img of imgs) {
        img.style.zIndex = '';
        img.style.opacity = '0';
      }
      if (ProductCard.#activeCycleCard === this) ProductCard.#activeCycleCard = null;
    };

    const activate = () => {
      // Only one card animates at a time — hand off from any previously tapped card
      const previous = ProductCard.#activeCycleCard;
      if (previous && previous !== this) previous.#colorCycle?.deactivate();
      ProductCard.#activeCycleCard = this;
      if (!running) {
        running = true;
        step();
      }
    };

    // Any touch on the card — including the start of a scroll gesture that lands on it —
    // starts the cycle for this specific product immediately. Taps on quick-add controls
    // are excluded; a tap that navigates makes this moot anyway.
    const onPointerDown = (/** @type {PointerEvent} */ event) => {
      if (event.pointerType !== 'touch') return;
      if (event.target instanceof Element && event.target.closest('button, input, label, select')) return;
      activate();
    };
    this.addEventListener('pointerdown', onPointerDown);
    const removeListeners = () => {
      this.removeEventListener('pointerdown', onPointerDown);
    };

    // Pre-warm the second colorway once the card is on screen so the first swap is
    // instant, and stand down if the highlighted card gets scrolled out of view.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!imgs[1].getAttribute('src')) {
            const source = sources[1];
            if (source.srcset) imgs[1].srcset = source.srcset;
            if (source.sizes) imgs[1].sizes = source.sizes;
            imgs[1].src = source.src ?? '';
          }
        } else {
          deactivate();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(cardGallery);

    // Once the user swipes through the card's own gallery, get out of their way for good
    slideshow.refs.scroller?.addEventListener('scroll', () => this.#destroyColorCycle(), { once: true, passive: true });

    this.#colorCycle = { overlay, observer, stop, deactivate, removeListeners };
  }

  /**
   * Stops the mobile colorway crossfade and removes its overlay.
   */
  #destroyColorCycle() {
    if (!this.#colorCycle) return;
    const { overlay, observer, deactivate, removeListeners } = this.#colorCycle;
    deactivate();
    observer.disconnect();
    removeListeners();
    overlay.remove();
    this.#colorCycle = null;
  }

  /** @type {Element | null} */
  #hoverColorSlide = null;

  /**
   * Re-hides the color variant slide that was temporarily shown during hover.
   */
  #restoreHoverColorSlide() {
    if (this.#hoverColorSlide) {
      this.#hoverColorSlide.setAttribute('hidden', '');
      this.#hoverColorSlide.setAttribute('aria-hidden', 'true');
      this.#hoverColorSlide = null;
    }
  }

  /**
   * The slides showing one image per colorway: each color's featured (mockup) image.
   * Falls back to all variant-attached images when the color list isn't available.
   * Other variant-attached media (e.g. size charts) are excluded.
   * @returns {Element[]}
   */
  #getColorSlides() {
    const { slideshow, cardGallery } = this.refs;
    const allSlides = slideshow?.refs?.slides ?? [];
    const colorMediaIds = (cardGallery?.dataset.colorMediaIds ?? '').split(',').filter(Boolean);

    const colorSlides = allSlides.filter((slide) =>
      colorMediaIds.includes(slide.getAttribute('slide-id') ?? '')
    );
    if (colorSlides.length >= 2) return colorSlides;

    return allSlides.filter((slide) => slide.hasAttribute('variant-image'));
  }

  /**
   * Finds the slide of another color variant's image, when the "next color on hover" setting is active.
   * Slides for non-selected color variants are rendered hidden, so this searches all slides, not just visible ones.
   * @returns {Element | null} The slide for a different color variant, or null.
   */
  #getNextColorSlide() {
    const { slideshow, cardGallery } = this.refs;

    if (cardGallery?.dataset.hoverImageStyle !== 'next_color') return null;
    if (!slideshow?.refs?.slides?.length) return null;

    const currentId =
      this.variantPicker?.selectedOption?.dataset.optionMediaId ??
      slideshow.slides?.[slideshow.current]?.getAttribute('slide-id');

    const colorSlides = this.#getColorSlides();

    // Prefer the merchant's chosen (most contrasting) colorway when one is set
    const preferredSwapId = cardGallery?.dataset.preferredSwapId;
    if (preferredSwapId && preferredSwapId !== currentId) {
      const preferred = colorSlides.find((slide) => slide.getAttribute('slide-id') === preferredSwapId);
      if (preferred) return preferred;
    }

    return colorSlides.find((slide) => slide.getAttribute('slide-id') !== currentId) ?? null;
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
      return;
    }

    const nextColorSlide = this.#getNextColorSlide();
    if (nextColorSlide) {
      const img = nextColorSlide.querySelector('img');
      if (img?.getAttribute('loading') === 'lazy') img.removeAttribute('loading');
      // The [reveal] mechanism can't override Tailwind's `[hidden] { display: none !important }`,
      // so temporarily un-hide the slide for the duration of the hover instead.
      if (nextColorSlide.hasAttribute('hidden')) {
        nextColorSlide.removeAttribute('hidden');
        nextColorSlide.setAttribute('aria-hidden', 'false');
        this.#hoverColorSlide = nextColorSlide;
      }
      slideshow.select({ id: nextColorSlide.getAttribute('slide-id') }, undefined, { animate: false });
      return;
    }

    slideshow.next(undefined, { animate: false });
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow, cardGallery } = this.refs;

    // In "next color on hover" mode the hovered slide is a hidden variant slide,
    // so always snap back to the selected variant (or initial) image by id.
    if (cardGallery?.dataset.hoverImageStyle === 'next_color') {
      this.#resetVariant();
      this.#restoreHoverColorSlide();
      return;
    }

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Persist the selection so it survives morphs
      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      if (optionValueId) {
        this.dataset.currentSelectedOptionId = optionValueId;
        this.dataset.currentSelectedOptions = optionValueId;
        // Emit selection event for any listeners that sync UI state
        this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));
      }

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
